import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute, { HOME_BY_ROLE } from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import ShopHeader from "@/components/ShopHeader";
import LegalGate from "@/components/LegalGate";
import { CUSTOMER_NAV, CUSTOMER_BOTTOM, AGENT_NAV, AGENT_BOTTOM, ADMIN_NAV, ADMIN_BOTTOM, ADMIN_DEFAULT_OPEN_SECTIONS } from "@/config/nav";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import LegalPage from "@/pages/LegalPage";
import LegalIndex from "@/pages/LegalIndex";
import CookieBanner from "@/components/CookieBanner";
import SharedDoc from "@/pages/SharedDoc";
import AISecretaryPage from "@/pages/AISecretaryPage";

import WhatsAppPage from "@/pages/WhatsAppPage";
// ConnectionsPage/ImportEmailPage/BookingTracker removed with legacy travel features
import ReportsPage from "@/pages/ReportsPage";
import ProfilePage from "@/pages/ProfilePage";
import PlansPage from "@/pages/PlansPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ResourcePage from "@/components/ResourcePage";

import CustomerHome from "@/pages/customer/CustomerHome";
import RewardsPage from "@/pages/customer/RewardsPage";
import DocumentsPage from "@/pages/customer/DocumentsPage";
import PublicQuote from "@/pages/PublicQuote";
import AuthCallback from "@/pages/AuthCallback";
import ChooseRole from "@/pages/ChooseRole";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ClaimAccount from "@/pages/ClaimAccount";
import PublicReadiness from "@/pages/PublicReadiness";
import MessageCenter from "@/pages/agent/MessageCenter";
import SupportCenter from "@/pages/SupportCenter";
import AdminSupportCenter from "@/pages/admin/AdminSupportCenter";
import HelpCenter from "@/pages/HelpCenter";

import AgentHome from "@/pages/agent/AgentHome";
import SellerDashboard from "@/pages/agent/SellerDashboard";
import SellerFleet from "@/pages/agent/SellerFleet";
import SellerAnalytics from "@/pages/agent/SellerAnalytics";
import InventoryPage from "@/pages/agent/InventoryPage";
import CommissionPage from "@/pages/agent/CommissionPage";
import SellerCustomers from "@/pages/agent/SellerCustomers";
import PosterMaker from "@/pages/agent/PosterMaker";
import TeamPage from "@/pages/agent/TeamPage";

import { AdminDashboard, AdminCustomers, AdminPlans, AdminAudit } from "@/pages/admin/AdminPages";
import { AdminCompany, AdminCoupons, AdminSupport } from "@/pages/admin/AdminExtra";
import { AdminLegal } from "@/pages/admin/AdminLegal";
import { AdminEmailHealth } from "@/pages/admin/AdminEmailHealth";
import { AdminEmailManager } from "@/pages/admin/AdminEmailManager";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminHomeCMS from "@/pages/admin/AdminHomeCMS";
import AdminFleet from "@/pages/admin/AdminFleet";
import AdminSearch from "@/pages/admin/AdminSearch";
import AdminDedupe from "@/pages/admin/AdminDedupe";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminShipping from "@/pages/admin/AdminShipping";
import AdminDirectShip from "@/pages/admin/AdminDirectShip";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import MyInvoicesPage from "@/pages/customer/MyInvoicesPage";
import AdminCheckout from "@/pages/admin/AdminCheckout";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminPayoutsReport from "@/pages/admin/AdminPayoutsReport";
import AdminRecommendations from "@/pages/admin/AdminRecommendations";
import AdminSponsored from "@/pages/admin/AdminSponsored";
import AdminAffiliate from "@/pages/admin/AdminAffiliate";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminRisk from "@/pages/admin/AdminRisk";
import { AdminMarketplace } from "@/pages/admin/AdminMarketplace";
import { AdminStorefronts } from "@/pages/admin/AdminStorefronts";
import { AdminWhatsAppPlans } from "@/pages/admin/AdminWhatsAppPlans";
import ShopPage from "@/pages/customer/ShopPage";
import FinancePage from "@/pages/FinancePage";
import CartPage from "@/pages/customer/CartPage";
import CheckoutPage from "@/pages/customer/CheckoutPage";
import VerifyMobile from "@/pages/VerifyMobile";
import GuestCartPage from "@/pages/GuestCartPage";
import MyOrdersPage from "@/pages/customer/MyOrdersPage";
import CustomerCustomRequests from "@/pages/customer/CustomerCustomRequests";
import OrderDetailPage from "@/pages/customer/OrderDetailPage";
import MyReviewsPage from "@/pages/customer/MyReviewsPage";
import WishlistPage from "@/pages/customer/WishlistPage";
import PaymentsPage from "@/pages/customer/PaymentsPage";
import ReturnsPage from "@/pages/customer/ReturnsPage";
import RecentlyViewedPage from "@/pages/customer/RecentlyViewedPage";
import SellerReviews from "@/pages/agent/SellerReviews";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminVerifiedAgents from "@/pages/admin/AdminVerifiedAgents";
import MarketplaceControlCenter from "@/pages/admin/AdminDashboard";
import AdminModeration from "@/pages/admin/AdminModeration";
import SellerOrders from "@/pages/agent/SellerOrders";
import SellerCustomRequests from "@/pages/agent/SellerCustomRequests";
import SellerOrderDetail from "@/pages/agent/SellerOrderDetail";
import { AdminOrders } from "@/pages/admin/AdminOrders";
import { AdminRefunds } from "@/pages/admin/AdminRefunds";
import { AdminSettlements } from "@/pages/admin/AdminSettlements";
import { AdminCommission } from "@/pages/admin/AdminCommission";
import { AdminPromotions } from "@/pages/admin/AdminPromotions";
import SellerSettlements from "@/pages/agent/SellerSettlements";
import SellerCredit from "@/pages/agent/SellerCredit";
import SellerPromotions from "@/pages/agent/SellerPromotions";
import SellerShipping from "@/pages/agent/SellerShipping";
import TrackOrder from "@/pages/TrackOrder";
import Storefront from "@/pages/Storefront";
import ProductPage from "@/pages/ProductPage";
import PublicShell from "@/components/PublicShell";
import ShipRatesPage from "@/pages/customer/ShipRatesPage";
import MyParcelsPage from "@/pages/customer/MyParcelsPage";
import MyCruisesPage from "@/pages/customer/MyCruisesPage";
import RfqPage from "@/pages/customer/RfqPage";
import RfqProviderPage from "@/pages/customer/RfqProviderPage";
import ParcelLabelPage from "@/pages/customer/ParcelLabelPage";
import DriverApp from "@/pages/DriverApp";
import SharedRideTrack from "@/pages/SharedRideTrack";
import AffiliateOut from "@/pages/AffiliateOut";
import PublicSellers from "@/pages/PublicSellers";
import PublicContact from "@/pages/PublicContact";
import DealsPage from "@/pages/DealsPage";
import SellerVerification from "@/pages/agent/SellerVerification";
import SellingAreas from "@/pages/agent/SellingAreas";
import SellerStorefront from "@/pages/agent/SellerStorefront";
import SellerPromote from "@/pages/agent/SellerPromote";
import SellerStorefrontPreview from "@/pages/agent/SellerStorefrontPreview";
import SellerWhatsAppPlans from "@/pages/agent/SellerWhatsAppPlans";
import SellerProducts from "@/pages/agent/SellerProducts";
import SellerProductWizard from "@/pages/agent/SellerProductWizard";
import SellerProductHub from "@/pages/agent/SellerProductHub";
import { AdminProducts } from "@/pages/admin/AdminProducts";
import { AdminSellerCredit } from "@/pages/admin/AdminSellerCredit";
import { AdminCatalog } from "@/pages/admin/AdminCatalog";
import { AdminVariantRequests } from "@/pages/admin/AdminVariantRequests";
import { AdminSellerVerification } from "@/pages/admin/AdminSellerVerification";
import ControlCenter from "@/pages/admin/ControlCenter";
import { AdminSubscriptions } from "@/pages/admin/AdminSubscriptions";
import { AdminProviders } from "@/pages/admin/AdminProviders";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminGoLive from "@/pages/admin/AdminGoLive";
import AdminCommRouting from "@/pages/admin/AdminCommRouting";
import AdminMessageGuide from "@/pages/admin/AdminMessageGuide";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminWhatsAppCenter from "@/pages/admin/AdminWhatsAppCenter";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminShoppingIntegrations from "@/pages/admin/AdminShoppingIntegrations";
import AdminProductFeeds from "@/pages/admin/AdminProductFeeds";
import AdminServiceIntegrations from "@/pages/admin/AdminServiceIntegrations";
import AdminMapsKeys from "@/pages/admin/AdminMapsKeys";
import AdminTravelLocations from "@/pages/admin/AdminTravelLocations";
import AdminServices from "@/pages/admin/AdminServices";
import AdminTravelListings from "@/pages/admin/AdminTravelListings";
import AdminHolidayBanner from "@/pages/admin/AdminHolidayBanner";
import SellerServices from "@/pages/agent/SellerServices";
import SellerServiceBookings from "@/pages/agent/SellerServiceBookings";
import SellerServiceCommission from "@/pages/agent/SellerServiceCommission";
import AdminServiceCommission from "@/pages/admin/AdminServiceCommission";
import CustomerBookings from "@/pages/customer/CustomerBookings";
import BookingConfirmed from "@/pages/customer/BookingConfirmed";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import ServicesBrowsePage from "@/pages/ServicesBrowsePage";
import CruisesPage from "@/pages/CruisesPage";
import CruiseDetailPage from "@/pages/CruiseDetailPage";
import SellerCruises from "@/pages/agent/SellerCruises";
import AdminCruises from "@/pages/admin/AdminCruises";
import HolidayDestinationsPage from "@/pages/HolidayDestinationsPage";
import EsimLandingPage from "@/pages/EsimLandingPage";
import HolidayDestinationPage from "@/pages/HolidayDestinationPage";
import AdminChatbotTraining from "@/pages/admin/AdminChatbotTraining";
import { AdminSupportInquiries, AdminEarnings, AdminProductGuard, AdminBroadcast } from "@/pages/admin/AdminModules";
import SeoHead from "@/components/SeoHead";
import AiAssistantLauncher from "@/components/AiAssistantLauncher";

function GuestOnly({ children }) {
  const { user, booting } = useAuth();
  if (booting) return null;
  if (user) return <Navigate to={user.role === "customer" ? "/" : (HOME_BY_ROLE[user.role] || "/app/home")} replace />;
  return children;
}

function CustomerShell() {
  return (
    <ProtectedRoute roles={["customer"]}>
      <LegalGate>
        <DashboardLayout nav={CUSTOMER_NAV} bottomNav={CUSTOMER_BOTTOM} portalLabel="Customer" aiSurface="customer" aiPlaceholder="Search products, brands, categories or ask Suffle AI…" />
      </LegalGate>
    </ProtectedRoute>
  );
}
// Storefront shell for logged-in shoppers: same clean top bar guests get, no sidebar.
function CustomerStoreShell() {
  return (
    <ProtectedRoute roles={["customer"]}>
      <LegalGate>
        <div className="min-h-screen bg-slate-50">
          <ShopHeader />
          <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6"><Outlet /></main>
        </div>
      </LegalGate>
    </ProtectedRoute>
  );
}
function AgentShell() {
  return (
    <ProtectedRoute roles={["travel_agent"]}>
      <LegalGate>
        <DashboardLayout nav={AGENT_NAV} bottomNav={AGENT_BOTTOM} portalLabel="Seller / Service Portal" aiSurface="agent"
          aiPlaceholder="Ask your AI Secretary... manage orders, list a product, grow your store..." />
      </LegalGate>
    </ProtectedRoute>
  );
}
function AdminShell() {
  return (
    <ProtectedRoute roles={["super_admin"]}>
      <DashboardLayout nav={ADMIN_NAV} bottomNav={ADMIN_BOTTOM} portalLabel="Super Admin" aiSurface="admin" aiPlaceholder="Search admin..." defaultOpenSections={ADMIN_DEFAULT_OPEN_SECTIONS} />
    </ProtectedRoute>
  );
}

// Legacy /agent/* URLs transparently redirect to the canonical /seller/* pages
// (role key + routes stay "agent" internally — zero migration risk).
// Ride share tokens are UUIDs; anything else on /track/ is a courier AWB.
function TrackSwitch() {
  const { token } = useParams();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(token || "") ? <SharedRideTrack /> : <TrackOrder />;
}

function AgentRedirect() {
  const loc = useLocation();
  return <Navigate to={loc.pathname.replace(/^\/agent/, "/seller") + loc.search} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors offset={72} />
          <CookieBanner />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
            <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
            <Route path="/legal" element={<LegalIndex />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/q/:token" element={<PublicQuote />} />
            <Route path="/driver" element={<DriverApp />} />
            <Route path="/track/:token" element={<TrackSwitch />} />
            <Route path="/out" element={<AffiliateOut />} />
            <Route path="/shared/doc/:token" element={<SharedDoc />} />
            <Route path="/dashboard" element={<AuthCallback />} />
            <Route path="/choose-role" element={<ChooseRole />} />
            <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/claim" element={<ClaimAccount />} />
            <Route path="/app/booking/:id/confirmed" element={<ProtectedRoute roles={["customer"]}><BookingConfirmed /></ProtectedRoute>} />
            <Route path="/trip-check/:token" element={<PublicReadiness />} />
            <Route path="/track" element={<TrackOrder />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/support-console" element={<ProtectedRoute roles={["super_admin", "support_agent"]}><AdminSupportCenter /></ProtectedRoute>} />
            <Route path="/verify-mobile" element={<ProtectedRoute><VerifyMobile /></ProtectedRoute>} />
            <Route path="/agent/*" element={<AgentRedirect />} />

            {/* Customer */}
            <Route path="/app">
              <Route index element={<Navigate to="home" replace />} />
              {/* Storefront (top bar, no sidebar) — the shopping surface */}
              <Route element={<CustomerStoreShell />}>
                <Route path="shop" element={<ShopPage />} />
                <Route path="search" element={<Navigate to="/app/shop" replace />} />
                <Route path="cart" element={<CartPage />} />
                <Route path="checkout" element={<CheckoutPage />} />
                <Route path="wishlist" element={<WishlistPage />} />
                <Route path="recently-viewed" element={<RecentlyViewedPage />} />
                <Route path="ai" element={<AISecretaryPage surface="customer" />} />
              </Route>
              {/* Account hub (sidebar layout) */}
              <Route element={<CustomerShell />}>
                <Route path="home" element={<CustomerHome />} />
                <Route path="shop-orders" element={<MyOrdersPage />} />
                <Route path="custom-orders" element={<CustomerCustomRequests />} />
                <Route path="orders/:orderId" element={<OrderDetailPage />} />
                <Route path="reviews" element={<MyReviewsPage />} />
                <Route path="bookings" element={<CustomerBookings />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="invoices" element={<MyInvoicesPage />} />
                <Route path="returns" element={<ReturnsPage />} />
                <Route path="subscriptions" element={<ResourcePage resourceKey="subscriptions" />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="whatsapp" element={<WhatsAppPage />} />
                <Route path="reminders" element={<ResourcePage resourceKey="reminders" />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="subscription" element={<PlansPage />} />
                <Route path="rewards" element={<RewardsPage />} />
                <Route path="support" element={<SupportCenter surface="customer" />} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>
            </Route>

            {/* Seller */}
              <Route path="/seller" element={<AgentShell />}>
              <Route index element={<Navigate to="home" replace />} />
              <Route path="home" element={<SellerDashboard />} />
              <Route path="analytics" element={<SellerAnalytics />} />
              <Route path="ai" element={<AISecretaryPage surface="agent" />} />
              <Route path="poster" element={<PosterMaker />} />
              <Route path="verification" element={<SellerVerification />} />
              <Route path="selling-areas" element={<SellingAreas />} />
              <Route path="storefront" element={<SellerStorefront />} />
              <Route path="promote" element={<SellerPromote />} />
              <Route path="storefront-preview" element={<SellerStorefrontPreview />} />
              <Route path="whatsapp-plans" element={<SellerWhatsAppPlans />} />
              <Route path="products" element={<SellerProductHub />} />
              <Route path="products/new" element={<SellerProductHub />} />
              <Route path="services" element={<SellerServices />} />
              <Route path="cruises" element={<SellerCruises />} />
              <Route path="fleet" element={<SellerFleet />} />
              <Route path="bookings" element={<SellerServiceBookings />} />
              <Route path="orders" element={<SellerOrders />} />
              <Route path="custom-requests" element={<SellerCustomRequests />} />
              <Route path="orders/:id" element={<SellerOrderDetail />} />
              <Route path="reviews" element={<SellerReviews />} />
              <Route path="settlements" element={<SellerSettlements />} />
              <Route path="invoices" element={<MyInvoicesPage />} />
              <Route path="credit" element={<SellerCredit />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="commission" element={<CommissionPage />} />
              <Route path="service-commission" element={<SellerServiceCommission />} />
              <Route path="customers" element={<SellerCustomers />} />
              <Route path="promotions" element={<SellerPromotions />} />
              <Route path="shipping" element={<SellerShipping />} />
              <Route path="whatsapp" element={<WhatsAppPage />} />
              <Route path="messages" element={<MessageCenter />} />
              <Route path="support" element={<SupportCenter surface="seller" />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="reports" element={<ReportsPage agent />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Super Admin */}
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<AdminDashboard />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="support" element={<AdminSupportCenter />} />
              <Route path="company" element={<AdminCompany />} />
              <Route path="legal" element={<AdminLegal />} />
              <Route path="email-health" element={<AdminEmailHealth />} />
              <Route path="email-manager" element={<AdminEmailManager />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="home-cms" element={<AdminHomeCMS />} />
              <Route path="search" element={<AdminSearch />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="shipping" element={<AdminShipping />} />
              <Route path="direct-ship" element={<AdminDirectShip />} />
              <Route path="invoices" element={<AdminInvoices />} />
              <Route path="checkout" element={<AdminCheckout />} />
              <Route path="finance" element={<AdminFinance />} />
              <Route path="payouts-report" element={<AdminPayoutsReport />} />
              <Route path="recommendations" element={<AdminRecommendations />} />
              <Route path="sponsored" element={<AdminSponsored />} />
              <Route path="affiliate" element={<AdminAffiliate />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="risk" element={<AdminRisk />} />
              <Route path="marketplace" element={<AdminMarketplace />} />
              <Route path="storefronts" element={<AdminStorefronts />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="dedupe" element={<AdminDedupe />} />
              <Route path="dashboard" element={<MarketplaceControlCenter />} />
              <Route path="moderation" element={<AdminModeration />} />
              <Route path="whatsapp-plans" element={<AdminWhatsAppPlans />} />
              <Route path="seller-verification" element={<AdminSellerVerification />} />
              <Route path="verified-agents" element={<AdminVerifiedAgents />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="seller-credit" element={<AdminSellerCredit />} />
              <Route path="catalog" element={<AdminCatalog />} />
              <Route path="variant-requests" element={<AdminVariantRequests />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="refunds" element={<AdminRefunds />} />
              <Route path="settlements" element={<AdminSettlements />} />
              <Route path="commission" element={<AdminCommission />} />
              <Route path="service-commission" element={<AdminServiceCommission />} />
              <Route path="promotions" element={<AdminPromotions />} />
              <Route path="control-center" element={<ControlCenter />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="providers" element={<AdminProviders />} />
              <Route path="integrations" element={<AdminIntegrations />} />
              <Route path="go-live" element={<AdminGoLive />} />
              <Route path="comm-routing" element={<AdminCommRouting />} />
              <Route path="message-guide" element={<AdminMessageGuide />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="whatsapp-center" element={<AdminWhatsAppCenter />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="shopping-integrations" element={<AdminShoppingIntegrations />} />
              <Route path="product-feeds" element={<AdminProductFeeds />} />
              <Route path="service-integrations" element={<AdminServiceIntegrations />} />
              <Route path="maps-keys" element={<AdminMapsKeys />} />
              <Route path="travel-locations" element={<AdminTravelLocations />} />
              <Route path="travel-listings" element={<AdminTravelListings />} />
              <Route path="cruises" element={<AdminCruises />} />
              <Route path="holiday-banner" element={<AdminHolidayBanner />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="fleet" element={<AdminFleet />} />
              <Route path="chatbot-training" element={<AdminChatbotTraining />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="inquiries" element={<AdminSupportInquiries />} />
              <Route path="earnings" element={<AdminEarnings />} />
              <Route path="product-guard" element={<AdminProductGuard />} />
              <Route path="broadcast" element={<AdminBroadcast />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>

            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/cart" element={<GuestCartPage />} />
            <Route path="/shop" element={<PublicShell><ShopPage /></PublicShell>} />
            <Route path="/shipping" element={<PublicShell><ShipRatesPage /></PublicShell>} />
            <Route path="/app/parcels" element={<ProtectedRoute><PublicShell><MyParcelsPage /></PublicShell></ProtectedRoute>} />
            <Route path="/app/cruises" element={<ProtectedRoute><PublicShell><MyCruisesPage /></PublicShell></ProtectedRoute>} />
            <Route path="/app/parcels/:id/label" element={<ProtectedRoute><ParcelLabelPage /></ProtectedRoute>} />
            <Route path="/rfq" element={<ProtectedRoute><PublicShell><RfqPage /></PublicShell></ProtectedRoute>} />
            <Route path="/rfq/provider" element={<ProtectedRoute><PublicShell><RfqProviderPage /></PublicShell></ProtectedRoute>} />
            <Route path="/finance" element={<PublicShell><FinancePage /></PublicShell>} />
            <Route path="/shop/search/:searchQuery" element={<PublicShell><ShopPage /></PublicShell>} />
            <Route path="/shop/:categorySlug" element={<PublicShell><ShopPage /></PublicShell>} />
            <Route path="/sellers" element={<PublicShell><PublicSellers /></PublicShell>} />
            <Route path="/contact" element={<PublicShell><PublicContact /></PublicShell>} />
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/services" element={<ServicesBrowsePage />} />
            <Route path="/cruises" element={<CruisesPage />} />
            <Route path="/cruises/:id" element={<CruiseDetailPage />} />
            <Route path="/travel" element={<ServicesBrowsePage />} />
            <Route path="/holidays" element={<HolidayDestinationsPage />} />
            <Route path="/esim" element={<EsimLandingPage />} />
            <Route path="/holidays/:slug" element={<HolidayDestinationPage />} />
            <Route path="/service/:slug" element={<ServiceDetailPage />} />
            <Route path="/:slug" element={<Storefront />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <SeoHead />
          <AiAssistantLauncher />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
